import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { assessmentService } from '../../services/api'
import { Timer, ProgressBar, QuestionDisplay, Results } from '../../components/assessments'
import {
  AssessmentState,
  AssessmentQuestion,
  AssessmentProgress,
  AssessmentResult,
} from '../../types/assessment'

type PageState = 'loading' | 'question' | 'complete' | 'error'

export function AssessmentPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [pageState, setPageState] = useState<PageState>('loading')
  const [error, setError] = useState<string | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Assessment state
  const [assessmentId, setAssessmentId] = useState<string | null>(id || null)
  const [skillName, setSkillName] = useState<string>('')
  const [question, setQuestion] = useState<AssessmentQuestion | null>(null)
  const [progress, setProgress] = useState<AssessmentProgress | null>(null)
  const [results, setResults] = useState<AssessmentResult | null>(null)
  const [presentedAt, setPresentedAt] = useState<string | null>(null)

  // Start a new assessment or load existing
  const initializeAssessment = useCallback(async () => {
    try {
      setPageState('loading')
      setError(null)

      if (id) {
        // Load existing assessment
        const response = await assessmentService.getNextQuestion(id)
        handleQuestionResponse(response.data)
      } else {
        // Start new assessment
        const skillId = searchParams.get('skill')
        if (!skillId) {
          setError('No skill specified')
          setPageState('error')
          return
        }

        const startResponse = await assessmentService.start(skillId)
        const newAssessmentId = startResponse.data.assessment_id
        setAssessmentId(newAssessmentId)

        // Get first question
        const questionResponse = await assessmentService.getNextQuestion(newAssessmentId)
        handleQuestionResponse(questionResponse.data)

        // Update URL without full navigation
        window.history.replaceState(null, '', `/expert/assessments/${newAssessmentId}`)
      }
    } catch (err: unknown) {
      console.error('Failed to initialize assessment:', err)
      const message = err instanceof Error ? err.message : 'Failed to load assessment'
      setError(message)
      setPageState('error')
    }
  }, [id, searchParams])

  // Handle question/complete response from API
  const handleQuestionResponse = (data: {
    question?: AssessmentQuestion
    progress: AssessmentProgress
    is_complete: boolean
    results?: AssessmentResult
    presented_at?: string
  }) => {
    setProgress(data.progress)

    if (data.is_complete) {
      setResults(data.results || null)
      setPageState('complete')
    } else if (data.question) {
      setQuestion(data.question)
      setPresentedAt(data.presented_at || new Date().toISOString())
      setPageState('question')
    }
  }

  // Submit answer
  const handleSubmitAnswer = async (selectedOptions: string[]) => {
    if (!assessmentId || !question || !presentedAt) return

    try {
      setIsSubmitting(true)
      const response = await assessmentService.submitAnswer(
        assessmentId,
        question.id,
        selectedOptions,
        presentedAt
      )
      handleQuestionResponse(response.data)
    } catch (err: unknown) {
      console.error('Failed to submit answer:', err)
      const message = err instanceof Error ? err.message : 'Failed to submit answer'
      setError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  // Handle time up
  const handleTimeUp = useCallback(async () => {
    if (!assessmentId) return

    try {
      // Check status - API should have timed out the assessment
      const response = await assessmentService.getStatus(assessmentId)
      if (response.data.is_complete) {
        setResults(response.data.results)
        setProgress(response.data.progress)
        setPageState('complete')
      }
    } catch (err) {
      console.error('Error checking timeout status:', err)
    }
  }, [assessmentId])

  // Initialize on mount
  useEffect(() => {
    initializeAssessment()
  }, [initializeAssessment])

  // Render based on state
  if (pageState === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
          <p className="mt-4 text-gray-600">Loading assessment...</p>
        </div>
      </div>
    )
  }

  if (pageState === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
            <svg
              className="w-8 h-8 text-red-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/expert/dashboard')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Return to Dashboard
          </button>
        </div>
      </div>
    )
  }

  if (pageState === 'complete' && results) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <Results
          result={results}
          skillName={skillName}
          onBack={() => navigate('/expert/dashboard')}
          onRetry={() => navigate('/expert/assessments')}
        />
      </div>
    )
  }

  // Question display
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header with timer and progress */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-lg font-semibold text-gray-900">
              {skillName || 'Skill Assessment'}
            </h1>
            {progress && (
              <Timer
                initialSeconds={progress.time_remaining_seconds}
                onTimeUp={handleTimeUp}
              />
            )}
          </div>
          {progress && <ProgressBar progress={progress} />}
        </div>
      </div>

      {/* Question area */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8">
          {question && (
            <QuestionDisplay
              question={question}
              onSubmit={handleSubmitAnswer}
              isSubmitting={isSubmitting}
            />
          )}
        </div>
      </div>

      {/* Exit confirmation modal would go here */}
    </div>
  )
}
